// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngZone from "@awc/App/Components/Common/XngZone";
import { CustomAppState } from "@awc/App/Store/Reducers";
import TabGroup, { TabGroupPresentationProps } from "@awc/MobiusOverrides/TabGroup/TabGroup";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab/Tab";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useLayoutEffect, useRef, useState } from "react";
import { shallowEqual } from "react-redux";

interface TabsStyles {
    tabsContainer?: TabGroupPresentationProps;
    wrapper?: InjectableCss;
    container?: InjectableCss;
    title?: TypographyPresentationProps;
    tabContentWrapper?: InjectableCss;
    tab?: TabPresentationProps;
}

export const tabsStyles: TabsStyles = {};

const styles = tabsStyles;

const enum tabFields {
    tabName = "tabName",
    backgroundColor = "backgroundColor",
}

const enum fields {
    title = "title",
    tabs = "tabs",
}

interface TabField {
    fields: {
        [tabFields.tabName]: string;
        [tabFields.backgroundColor]: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabField[];
    };
}
type Props = OwnProps;

const XTabs: FC<Props> = ({ fields: { tabs = [], title }, id }) => {
    const isEditing = useShellContext().isEditing;
    const tabContainerRef = useRef<HTMLDivElement>(null);
    const [tabsState, setTabsState] = useState<TabField[]>([]);
    const [tabVisibility, setTabVisibility] = useState<{ [tab: string]: boolean }>({});
    const refs: React.RefObject<{ [tab: string]: any }> = useRef({});
    const _tabsState = useAppSelector((state: ApplicationState) => (state as CustomAppState).xTabs, shallowEqual);

    useEffect(() => {
        setTabsState(tabs);
    }, [isEditing, tabs]);
    const isVisibleTab = (tab: TabField) => {
        return (
            !!_tabsState[`tab_${tab.fields.tabName}`] ||
            !!refs.current?.[tab.fields.tabName]?.children?.[0]?.children?.length
        );
    };

    useLayoutEffect(() => {
        const _tabVisibility = tabsState.reduce((acc, tab) => {
            acc[tab.fields.tabName] = isVisibleTab(tab);
            return acc;
        }, {} as { [tab: string]: boolean });
        setTabVisibility(_tabVisibility);
    }, [_tabsState, tabsState]);

    const atLeastOneVisible = isEditing || !!tabsState.find(tab => isVisibleTab(tab));
    return (
        <StyledWrapper
            {...styles.wrapper}
            ref={tabContainerRef}
            key={`tabsWrapper_${tabsState.length}`}
            {...{ isLoading: !atLeastOneVisible }}
        >
            {!!tabsState.length && (
                <StyledWrapper {...styles.container}>
                    {atLeastOneVisible && !!title && <Typography {...styles.title}>{title}</Typography>}
                    <TabGroup {...styles.tabsContainer}>
                        {tabsState.map(tab => (
                            <Tab
                                key={`${tab.fields.tabName}`}
                                headline={tab.fields.tabName}
                                tabKey={tab.fields.tabName}
                                aria-label={`${tab.fields.tabName} tab`}
                                hidden={!isEditing && !tabVisibility[tab.fields.tabName]}
                                {...styles.tab}
                            >
                                <StyledWrapper
                                    ref={el => {
                                        if (refs.current) {
                                            refs.current[tab.fields.tabName] = el;
                                        }
                                    }}
                                    {...styles.tabContentWrapper}
                                    {...{ backgroundColor: tab.fields.backgroundColor }}
                                >
                                    <XngZone
                                        contentId={id}
                                        zoneName={`tab_${tab.fields.tabName}`}
                                        hint={`Content for ${tab.fields.tabName}`}
                                    />
                                </StyledWrapper>
                            </Tab>
                        ))}
                    </TabGroup>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};
const xTabWidget: WidgetModule = {
    component: XTabs,
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                editorTemplate: "ListField",
                name: fields.tabs,
                getDisplay: (item: any) => item.fields.tabName,
                defaultValue: [],
                fieldType: "General",
                isRequired: true,
                fieldDefinitions: [
                    {
                        name: tabFields.tabName,
                        editorTemplate: "TextField",
                        displayName: "Tab Name",
                        isRequired: true,
                        defaultValue: "",
                    },
                    {
                        name: tabFields.backgroundColor,
                        editorTemplate: "ColorPickerField",
                        displayName: "Background Color",
                        defaultValue: "white",
                    },
                ],
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default xTabWidget;
